import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ArticlesCreateUserFeedResponse,
  ArticlesFeedCommentPopulatedUserResponse,
  ArticlesFeedTaggableUserResponse,
  ArticlesGetCommentAnswersUserResponse,
  ArticlesGetCommentsUserResponse,
  ArticlesListFeedQuery,
  ArticlesListFeedTaggableUsersQuery,
  ArticlesPaginatedFeedLikeUserResponse,
  ArticlesPaginatedPopulatedFeedAppResponse,
  ArticlesPopulatedFeedAppFullResponse,
  ArticlesPresignedStorageResource,
  ArticlesUpdateUserFeedBody,
} from '@libs/generated-types/articles';
import { Observable } from 'rxjs';
import { EnvironmentService } from '../@core/services/environment.service';

@Injectable({
  providedIn: 'root',
})
export class FeedService {
  private readonly apiUrl: string = '';

  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService,
  ) {
    this.apiUrl = this.environmentService.articlesServiceUrl + '/app';
  }

  getFeeds(query: ArticlesListFeedQuery): Observable<ArticlesPaginatedPopulatedFeedAppResponse> {
    const params = { ...query };
    return this.http.get<ArticlesPaginatedPopulatedFeedAppResponse>(`${this.apiUrl}/feeds`, { params });
  }

  getFeedsBoard(query: ArticlesListFeedQuery): Observable<ArticlesPaginatedPopulatedFeedAppResponse> {
    const params = { ...query };
    return this.http.get<ArticlesPaginatedPopulatedFeedAppResponse>(`${this.apiUrl}/board`, { params });
  }

  getFeedsHomepage(query: ArticlesListFeedQuery): Observable<ArticlesPaginatedPopulatedFeedAppResponse> {
    const params = { ...query };
    return this.http.get<ArticlesPaginatedPopulatedFeedAppResponse>(`${this.apiUrl}/feeds/homepage`, { params });
  }

  getFeedById(id: string): Observable<ArticlesPopulatedFeedAppFullResponse> {
    return this.http.get<ArticlesPopulatedFeedAppFullResponse>(`${this.apiUrl}/feeds/${id}`);
  }

  getFeedComments(id: string, query: any): Observable<ArticlesGetCommentsUserResponse> {
    return this.http.get<ArticlesGetCommentsUserResponse>(`${this.apiUrl}/feeds/${id}/comments`, {
      params: query,
    });
  }

  getFeedRelatedContents(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/feeds/${id}/related-contents`);
  }

  addLikeToFeed(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/feeds/${id}/likes`, {});
  }

  removeLikeFromFeed(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/feeds/${id}/likes`, {});
  }

  addCommentToFeed(id: string, text: string, attachment?: string, parentId?: string): Observable<ArticlesFeedCommentPopulatedUserResponse> {
    return this.http.post<ArticlesFeedCommentPopulatedUserResponse>(`${this.apiUrl}/feeds/${id}/comments`,
      { text, image: attachment && attachment.length > 0 ? attachment : undefined, parentId: parentId || undefined }
    );
  }

  addReactionToFeedComment(feedId: string, commentId: string, reaction: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/feeds/${feedId}/comments/${commentId}/reactions`, { reaction });
  }

  removeReactionFromFeedComment(feedId: string, commentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/feeds/${feedId}/comments/${commentId}/reactions`);
  }

  updateComment(feedId: string, commentId: string, text: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/feeds/${feedId}/comments/${commentId}`, { text });
  }

  deleteComment(feedId: string, commentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/feeds/${feedId}/comments/${commentId}`, {});
  }

  getPresignedUrlForImageUpload(feedId: string): Observable<{ key: string; presignedUrl: string }> {
    return this.http.post<{ key: string; presignedUrl: string }>(`${this.apiUrl}/feeds/${feedId}/comments/upload-image`, {});
  }

  getPresignedUrlForFeedCoverUpload(): Observable<ArticlesPresignedStorageResource> {
    return this.http.post<ArticlesPresignedStorageResource>(`${this.apiUrl}/feeds/upload-image`, {});
  }

  getPresignedUrlForFeedVideoUpload(): Observable<ArticlesPresignedStorageResource> {
    return this.http.post<ArticlesPresignedStorageResource>(`${this.apiUrl}/feeds/upload-video`, {});
  }

  getFeedVideoUrl(feedId: string): Observable<ArticlesPresignedStorageResource> {
    return this.http.get<ArticlesPresignedStorageResource>(`${this.apiUrl}/feeds/${feedId}/video-url`);
  }

  getUsersLikedPost(
    feedId: string,
    page = 0,
    size = 10,
    dateTo?: string,
  ): Observable<ArticlesPaginatedFeedLikeUserResponse> {
    const params = new HttpParams()
      .set('getTotalCount', 'true')
      .set('page', page)
      .set('size', size);
    const requestParams = dateTo === undefined ? params : params.set('dateTo', dateTo);

    return this.http.get<ArticlesPaginatedFeedLikeUserResponse>(
      `${this.apiUrl}/feeds/${feedId}/likes`,
      { params: requestParams }
    );
  }

  getCommentLikes(feedId: string, commentId: string, page = 1, size = 10) {
    const params = new HttpParams()
      .set('getTotalCount', 'true')
      .set('page', page)
      .set('size', size);

    return this.http.get<ArticlesPaginatedFeedLikeUserResponse>(
      `${this.apiUrl}/feeds/${feedId}/comments/${commentId}/likes`,
      { params }
    );
  }

  async putPresignedAsync(file: File, presignedUrl: string): Promise<void> {
    const response: Response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Presigned upload failed with status ${response.status} ${response.statusText}`);
    }
  }

  createUserFeed(payload: {
    group: string;
    title: string;
    description: string;
    cover?: string;
    containsVideo?: boolean;
    videoKey?: string;
  }): Observable<ArticlesCreateUserFeedResponse> {
    return this.http.post<ArticlesCreateUserFeedResponse>(`${this.apiUrl}/feeds`, payload);
  }

  updateUserFeed(id: string, payload: ArticlesUpdateUserFeedBody): Observable<ArticlesCreateUserFeedResponse> {
    return this.http.put<ArticlesCreateUserFeedResponse>(`${this.apiUrl}/feeds/${id}`, payload);
  }

  deleteUserFeed(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/feeds/${id}`);
  }

  getTaggableUsers(
    feedId: string,
    query: ArticlesListFeedTaggableUsersQuery
  ): Observable<ArticlesFeedTaggableUserResponse[]> {
    return this.http.get<ArticlesFeedTaggableUserResponse[]>(
      `${this.apiUrl}/feeds/${feedId}/taggable-users`,
      { params: { ...query } }
    );
  }

  getCommentReplies(id: string, commentId: string, query: any): Observable<ArticlesGetCommentAnswersUserResponse> {
    return this.http.get<ArticlesGetCommentAnswersUserResponse>(
      `${this.apiUrl}/feeds/${id}/comments/${commentId}/answers`,
      { params: query },
    );
  }

}
